import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export const ROL_SOCIO_ID = 36;

export interface RolesAdicionales {
  /**
   * rol_id activos en `user_roles` para el email propio (roles ADICIONALES al
   * `usuarios.rol_id`). null mientras consulta.
   */
  roles: number[] | null;
  /**
   * Rol 36 Socio como principal o como adicional. La BD lo da/quita sola a los
   * fideicomisarios de una asignación (nunca toca `usuarios.rol_id`).
   * null mientras consulta.
   */
  hasSocioRole: boolean | null;
  /**
   * ¿Tiene el rol `rolId`, como activo (`usuarios.rol_id`) o asignado en
   * `user_roles`? null mientras consulta.
   */
  tieneRol: (rolId: number) => boolean | null;
  /** ¿Tiene alguno de `rolIds` (activo o asignado)? null mientras consulta. */
  tieneAlgunRol: (rolIds: readonly number[]) => boolean | null;
}

/**
 * Una sola consulta a `user_roles` (RLS: cada usuario lee sus filas) para todos
 * los roles duales. react-query la comparte entre PermissionRoute, Login, etc.
 * Un error no deja la pantalla en "pending" para siempre: se trata como sin roles.
 */
export function useRolesAdicionales(): RolesAdicionales {
  const { profile } = useAuth();
  const email = profile?.email?.toLowerCase() ?? null;

  const { data } = useQuery({
    queryKey: ['user_roles_adicionales', email],
    enabled: !!email,
    staleTime: 60_000,
    queryFn: async (): Promise<number[]> => {
      const { data: rows, error } = await (supabase as any)
        .from('user_roles')
        .select('rol_id')
        .eq('email', email)
        .eq('activo', true);
      if (error) return [];
      return ((rows ?? []) as { rol_id: number }[]).map((r) => Number(r.rol_id));
    },
  });

  // Sin email no hay nada que consultar (mismo criterio que useHasEmbajadorRole).
  const roles: number[] | null = !email ? [] : (data ?? null);
  const hasSocioRole: boolean | null =
    profile?.rol_id === ROL_SOCIO_ID ? true : roles === null ? null : roles.includes(ROL_SOCIO_ID);

  const tieneAlgunRol = (rolIds: readonly number[]): boolean | null => {
    if (profile?.rol_id != null && rolIds.includes(profile.rol_id)) return true;
    if (roles === null) return null;
    return roles.some((r) => rolIds.includes(r));
  };
  const tieneRol = (rolId: number) => tieneAlgunRol([rolId]);

  return { roles, hasSocioRole, tieneRol, tieneAlgunRol };
}
